import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Save, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import axios, { AxiosError } from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { BandRecommendationFormData } from '../../types/BandRecommendations';

const DRAFT_ID_KEY = 'bandRecommendationDraftId';

interface FormSaveHandlerProps {
  formData: BandRecommendationFormData;
  formId?: string;
  isEditMode: boolean;
  enabled?: boolean;
  autoSaveInterval?: number;
  onDraftSaved?: (id: string) => void;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

function debounce<T extends (...args: any[]) => void>(func: T, delay: number): T {
  let timeoutId: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
}

const FormSaveHandler: React.FC<FormSaveHandlerProps> = ({
  formData,
  formId,
  isEditMode,
  enabled = true,
  autoSaveInterval = 60000,
  onDraftSaved,
}) => {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const { getToken } = useAuth();
  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const lastSavedData = useRef<string>('');
  const createdDraftId = useRef<string | null>(null);

  // Require at least 3 chars in artistName to avoid saving single-keystroke drafts
  const validateFormData = useCallback((data: BandRecommendationFormData): boolean => {
    return !!(data.artistName?.trim().length >= 3 && data.recommenderEmail?.trim());
  }, []);

  const hasDataChanged = useCallback((data: BandRecommendationFormData): boolean => {
    return JSON.stringify(data) !== lastSavedData.current;
  }, []);

  const saveFormData = useCallback(
    async (force = false, retryCount = 0) => {
      if (!enabled || !validateFormData(formData)) return;
      if (!force && !hasDataChanged(formData)) return;

      try {
        setSaveStatus('saving');
        const token = await getToken();
        if (!token) throw new Error('Authentication required');

        const config = {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          withCredentials: true,
        };

        const existingId = formId || createdDraftId.current;
        const endpoint = existingId
          ? `${process.env.REACT_APP_API_URL}/api/bandrecommendations/${existingId}`
          : `${process.env.REACT_APP_API_URL}/api/bandrecommendations/draft`;
        const method = existingId ? 'put' : 'post';

        const response = await axios[method](endpoint, formData, config);

        if (!existingId && response.data?._id) {
          createdDraftId.current = response.data._id;
          localStorage.setItem(DRAFT_ID_KEY, response.data._id);
          onDraftSaved?.(response.data._id);
        }

        lastSavedData.current = JSON.stringify(formData);
        setSaveStatus('saved');
        setLastSaved(new Date());
        setErrorMsg(null);
        setTimeout(() => setSaveStatus('idle'), 4000);
      } catch (err) {
        const error = err as AxiosError;

        if (retryCount < 2 && (error.code === 'NETWORK_ERROR' || error.response?.status === 500)) {
          setTimeout(() => saveFormData(false, retryCount + 1), 2000);
          return;
        }

        let msg = 'Auto-save failed';
        if (error.response?.status === 401) msg = 'Session expired — please log in again';
        else if (error.response?.status === 413) msg = 'Form data too large';

        // Mark data as "seen" so the interval doesn't keep retrying the same failed state.
        // The retry button passes force=true to bypass this check when the user explicitly retries.
        lastSavedData.current = JSON.stringify(formData);
        setErrorMsg(msg);
        setSaveStatus('error');
      }
    },
    [formId, enabled, getToken, formData, validateFormData, hasDataChanged, onDraftSaved]
  );

  const debouncedSave = useCallback(
    debounce(() => saveFormData(false), 3000),
    [saveFormData]
  );

  const cleanup = useCallback(() => {
    if (autoSaveTimer.current) { clearInterval(autoSaveTimer.current); autoSaveTimer.current = null; }
  }, []);

  useEffect(() => {
    if (enabled && formData && hasDataChanged(formData)) debouncedSave();
  }, [formData, enabled, debouncedSave, hasDataChanged]);

  useEffect(() => {
    if (!enabled) return;
    cleanup();
    autoSaveTimer.current = setInterval(() => saveFormData(false), autoSaveInterval);
    return cleanup;
  }, [enabled, autoSaveInterval, saveFormData, cleanup]);

  useEffect(() => {
    if (!enabled) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (saveStatus === 'saving') { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveStatus, enabled]);

  useEffect(() => cleanup, [cleanup]);

  if (!enabled) return null;

  // ─── Subtle status chip — bottom-right, never blocking ───────────────────────

  const formatTime = (d: Date) => {
    const secs = Math.floor((Date.now() - d.getTime()) / 1000);
    if (secs < 60) return 'just now';
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: saveStatus === 'error' ? 'error.main' : 'divider',
        borderRadius: 5,
        px: 1.5,
        py: 0.5,
        boxShadow: 1,
        opacity: saveStatus === 'idle' ? 0.55 : 1,
        transition: 'opacity 0.3s',
        '&:hover': { opacity: 1 },
      }}
    >
      {saveStatus === 'saving' && (
        <>
          <CircularProgress size={12} sx={{ color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">Saving…</Typography>
        </>
      )}

      {saveStatus === 'saved' && (
        <>
          <CheckCircle size={13} color="green" />
          <Typography variant="caption" color="success.main">
            Saved {lastSaved ? formatTime(lastSaved) : ''}
          </Typography>
        </>
      )}

      {saveStatus === 'error' && (
        <>
          <AlertCircle size={13} color="red" />
          <Typography variant="caption" color="error">{errorMsg}</Typography>
          <Tooltip title="Retry save">
            <IconButton size="small" onClick={() => saveFormData(true)} sx={{ p: 0.25 }}>
              <RefreshCw size={12} />
            </IconButton>
          </Tooltip>
        </>
      )}

      {saveStatus === 'idle' && (
        <>
          <Save size={12} style={{ opacity: 0.4 }} />
          <Typography variant="caption" color="text.disabled">
            {lastSaved ? `Saved ${formatTime(lastSaved)}` : 'Auto-save on'}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default React.memo(FormSaveHandler);
